import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type ListSeparator = "," | ";";

interface Settings {
  listSeparator: ListSeparator;
}

interface SettingsContextType extends Settings {
  setListSeparator: (separator: ListSeparator) => void;
}

const STORAGE_KEY = "excelImparable.settings";

function readStoredSettings(): Partial<Settings> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<Settings>) : {};
  } catch {
    return {};
  }
}

function storeSettings(settings: Settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage blocked — the choice just won't survive a reload.
  }
}

/**
 * Excel does not expose the list separator, but a "," decimal separator
 * almost always comes with ";" between arguments. Only used as a first guess.
 */
async function guessListSeparator(): Promise<ListSeparator | null> {
  if (typeof Excel === "undefined") return null;
  try {
    return await Excel.run(async (context) => {
      const app = context.application;
      app.load("decimalSeparator");
      await context.sync();
      return app.decimalSeparator === "," ? ";" : ",";
    });
  } catch {
    return null;
  }
}

const SettingsContext = createContext<SettingsContextType | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const stored = readStoredSettings();
  const [listSeparator, setSeparatorState] = useState<ListSeparator>(
    stored.listSeparator === ";" ? ";" : ","
  );

  // Only on first load, and only when the user never chose.
  useEffect(() => {
    if (stored.listSeparator) return;
    void guessListSeparator().then((guess) => {
      if (guess) setSeparatorState(guess);
    });
  }, []);

  const setListSeparator = useCallback((separator: ListSeparator) => {
    setSeparatorState(separator);
    storeSettings({ listSeparator: separator });
  }, []);

  return (
    <SettingsContext.Provider value={{ listSeparator, setListSeparator }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) throw new Error("useSettings must be used within SettingsProvider");
  return context;
}
